import React, { useState, useRef, useEffect } from 'react';
import { styled } from 'baseui';
import { lightTheme } from '@/customTheme';
import { hexToRgba } from '@/scenes/engine/utils/utils';
import WrapPopover, { PopoverGapType, PopoverPaddingType, PopoverWidthType } from './WrapPopover';
import { CategoryItem } from '../CategoryHorizontalScroller';
import { SkeletonItem } from './SkeletonItem';
import PlusIcon from '../../../Icons/PlusIcon';
import GradientLoader from '@/components/GradientLoader';

const GridItemWrapper = styled('div', ({ $aspectRatio, $selected }: { $aspectRatio?: number | string; $selected?: boolean }) => ({
  cursor: 'pointer',
  borderRadius: '8px',
  overflow: 'hidden',
  position: 'relative',
  boxSizing: 'border-box',
  '::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '8px',
    pointerEvents: 'none',
    background: 'transparent',
    // Picked: a 2px pink ring inside a 2px white one.
    boxShadow: $selected
      ? `inset 0 0 0 2px ${lightTheme.colors.primary}, inset 0 0 0 4px ${lightTheme.colors.white}`
      : `inset 0 0 0 1px ${hexToRgba(lightTheme.colors.black, 0.1)}`,
  },
  aspectRatio: $aspectRatio ? String($aspectRatio) : 'auto',
  ':hover': {
    '::after': {
      background: hexToRgba(lightTheme.colors.black, 0.2),
      boxShadow: `inset 0 0 0 1px ${hexToRgba(lightTheme.colors.black, 0.1)}`,
    },
  },
}));

interface GridItemProps {
  src: string;
  aspectRatio?: number | string;
  onClick: () => void;
  topHex?: string;
  bottomHex?: string;
  selected?: boolean;
}

const GridItem: React.FC<GridItemProps> = ({ src, aspectRatio, onClick, topHex, bottomHex, selected }) => (
  <GridItemWrapper $aspectRatio={aspectRatio} $selected={selected} onClick={onClick}>
    {topHex && bottomHex && !src ? (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: `linear-gradient(to bottom, ${topHex}, ${bottomHex})`,
        }}
      />
    ) : (
      <img
        src={src}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    )}
  </GridItemWrapper>
);

const LoadMoreCell = styled('div', ({ $aspectRatio, $disabled }: { $aspectRatio?: number | string; $disabled?: boolean }) => ({
  cursor: $disabled ? 'default' : 'pointer',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  aspectRatio: $aspectRatio ? String($aspectRatio) : 'auto',
  background: lightTheme.colors.grayScale150,
  opacity: $disabled ? 0.5 : 1,
  pointerEvents: $disabled ? 'none' as const : 'auto' as const,
  ':hover': $disabled ? {} : { background: lightTheme.colors.grayScale200 },
  ':active': $disabled ? {} : { background: lightTheme.colors.grayScale250 },
}));

const GradientSkeletonItem: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', ...style }}>
    <GradientLoader angle={0} borderRadius={8} fillContainer={true} />
  </div>
);

export interface ImagesGridPopoverObject {
  src: string;
  title: any;
  onClick?: () => void;
  categoryId?: string;
  topHex?: string;
  bottomHex?: string;
}

interface ImagesGridProps {
  objects: ImagesGridPopoverObject[];
  onSelectItem: (value: any) => void;
  onClose?: () => void;
  columns: number;
  aspectRatio?: number | string;
  /** Number of skeleton items to show while loading. Defaults to columns * 4. */
  skeletonCount?: number;
  onLoadMore?: (onComplete?: () => void) => void;
  isLoadingMore?: boolean;
  loadMoreSkeletonCount?: number;
  skeletonVariant?: 'default' | 'gradient';
  /** Marks the picked item with a ring; no item is marked when omitted. */
  isSelected?: (item: ImagesGridPopoverObject) => boolean;
}

export const ImagesGrid: React.FC<ImagesGridProps> = ({
  objects,
  onSelectItem,
  onClose,
  columns,
  aspectRatio,
  skeletonCount,
  onLoadMore,
  isLoadingMore,
  loadMoreSkeletonCount,
  skeletonVariant,
  isSelected,
}) => {
  const ghostCount = skeletonCount ?? columns * 4
  const Skeleton = skeletonVariant === 'gradient' ? GradientSkeletonItem : SkeletonItem

  const moreSkeletons = isLoadingMore ? (loadMoreSkeletonCount ?? 5) : 0

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '8px',
      }}
    >
      {objects.length === 0
        ? Array.from({ length: ghostCount }).map((_, index) => (
            <Skeleton key={`sk-${index}`} style={{ aspectRatio: aspectRatio || 'auto', width: '100%' }} />
          ))
        : objects.map((item, index) => (
            <GridItem
              key={index}
              src={item.src}
              aspectRatio={aspectRatio}
              topHex={item.topHex}
              bottomHex={item.bottomHex}
              selected={isSelected?.(item)}
              onClick={() => {
                if (item.onClick) {
                  item.onClick()
                } else {
                  onSelectItem(item.title)
                }
                onClose?.()
              }}
            />
          ))}
      {moreSkeletons > 0 &&
        Array.from({ length: moreSkeletons }).map((_, index) => (
          <Skeleton key={`load-more-sk-${index}`} style={{ aspectRatio: aspectRatio || 'auto', width: '100%' }} />
        ))}
      {onLoadMore && (
        <LoadMoreCell
          $aspectRatio={aspectRatio}
          $disabled={isLoadingMore || objects.length === 0}
          onClick={() => {
            if (!isLoadingMore) {
              onLoadMore(onClose ?? (() => {}))
            }
          }}
        >
          <PlusIcon />
        </LoadMoreCell>
      )}
    </div>
  );
};

interface ImagesGridPopoverProps extends ImagesGridProps{
  width: PopoverWidthType;
  height?: number;
  maxHeight?: number;
  buttonRef: React.RefObject<HTMLElement>;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  padding?: PopoverPaddingType;
  gap?: PopoverGapType;
  itemGap?: number
  title?: string
  showCloseButton?: boolean
  onClose?: () => void
  children?: React.ReactNode
  categories?: CategoryItem[]
  /** When true, scrolls the scroll container to the bottom on mount */
  scrollToBottom?: boolean
}

const ImagesGridPopover: React.FC<ImagesGridPopoverProps> = ({
  objects,
  onSelectItem,
  columns,
  aspectRatio,
  skeletonCount,
  width,
  height,
  maxHeight,
  buttonRef,
  placement,
  padding,
  gap,
  title,
  showCloseButton,
  onClose,
  children,
  categories,
  onLoadMore,
  isLoadingMore,
  loadMoreSkeletonCount,
  skeletonVariant,
  scrollToBottom,
}) => {
  const [activeCategoryId, setActiveCategoryId] = useState(categories?.[0]?.id ?? '')
  const [loadMoreScrollTrigger, setLoadMoreScrollTrigger] = useState<number>()
  const prevFilteredObjectsLengthRef = useRef(0)

  const filteredObjects = activeCategoryId && categories && categories.length > 0
    ? objects.filter((item) => !item.categoryId || item.categoryId === activeCategoryId)
    : objects

  useEffect(() => {
    const prevLength = prevFilteredObjectsLengthRef.current
    prevFilteredObjectsLengthRef.current = filteredObjects.length

    if (isLoadingMore || (filteredObjects.length > prevLength && prevLength > 0)) {
      setLoadMoreScrollTrigger((trigger) => (trigger ?? 0) + 1)
    }
  }, [filteredObjects.length, isLoadingMore])

  return (
    <WrapPopover
      width={width}
      height={height}
      maxHeight={maxHeight}
      buttonRef={buttonRef}
      placement={placement}
      padding={padding}
      gap={gap}
      title={title}
      onClose={onClose}
      showCloseButton={showCloseButton}
      categories={categories}
      onCategoryClick={setActiveCategoryId}
      scrollToBottom={scrollToBottom}
      scrollToBottomTrigger={loadMoreScrollTrigger}
    >
      {children}
      <ImagesGrid
        objects={filteredObjects}
        onSelectItem={onSelectItem}
        onClose={onClose}
        columns={columns}
        aspectRatio={aspectRatio}
        skeletonCount={skeletonCount}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        loadMoreSkeletonCount={loadMoreSkeletonCount}
        skeletonVariant={skeletonVariant}
      />
    </WrapPopover>
  );
};

export default ImagesGridPopover
export { GridItem }
